import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as Blockly from "blockly";
import {
  getAchievements,
  getScore,
  setQuizResult,
  getQuizResults,
  getQuizQuestions,
  idleShutDown,
} from "@/api";

import {
  useEditorState,
  type CircleFlowGroup,
} from "../store";

import { Actions } from "flexlayout-react";
import {
  postMessageToRef,
  postMessageToParent,
  downloadBlocklyScreenshot,
  asyncExecutionQue,
  FileSystemError,
  CommunicationError,
  AchievementError,
  IframeError
} from "@/utils";
import { QA } from "@/components";
import { Toolkit } from "@/components/Toolkit/Toolkit";
import { throttle } from "lodash";
import sessionSocket from "@/singletons/SessionSocket";
import { FS } from "@/singletons/FileSystem";
import * as Sentry from "@sentry/react";


function debounce(func: any, wait: number | undefined) {
  let timeout: number | undefined;

  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait) as unknown as number;
  };
}

function registerAchievementLocally(achievementId: number | string) {
  let pendingAchievementsStr = "[]";
  let pendingAchievements: any[] = [];
  try {
    pendingAchievementsStr = decodeURIComponent(localStorage.getItem("pendingAchievements") || "[]");
    pendingAchievements = JSON.parse(pendingAchievementsStr);

    if (!pendingAchievements.includes(achievementId)) {
      pendingAchievements.push(achievementId);
    }

  } catch (e) {
    pendingAchievements = [];
    pendingAchievementsStr = "[]";

    Sentry.addBreadcrumb({
      category: "achievement",
      message: "Failed to parse pending achievements",
      data: {
        achievementId,
        pendingAchievementsStr,
      },
    });
    Sentry.captureException(new AchievementError("Failed to parse pending achievements", e as Error));
  }


  pendingAchievementsStr = JSON.stringify(pendingAchievements);
  localStorage.setItem("pendingAchievements", encodeURIComponent(pendingAchievementsStr));
}

export interface CommunicationContextProps {
  children?: React.ReactNode;
  className?: string;
  project?: any;
  env?: any;
}

export interface ICommunicationContext extends React.FC<CommunicationContextProps> {
  context: React.Context<CommunicationContextProps>;
}

export const CommunicationContext = React.createContext({});
export const Communication: ICommunicationContext = ({ children }) => {
  const { t } = useTranslation();
  const { showModal } = useContext<any>(Toolkit.context);

  const {
    project,
    tokens: { express_api_token: accessToken } = {},
    godotContainerRef,
    setBlocklyRefreshKey,
    language,
    setLayout,
    setCircleFlow,
    setCollapsedSidebar,
    layout: { id: layoutId, model },
    shutDown: sh,
    encryptMessage,
    parseAchievementId,
    parseQAId,
    calculateLtiScore
  } = useEditorState();

  const heartbeat = useCallback(() => {
    if (sh) return;
    sessionSocket.sendHeartbeat();
  }, [sh]);

  const debouncedHeartbeat = useMemo(() => {
    return throttle(heartbeat, 30_000, {
      leading: true,
      trailing: false,
    });
  }, [heartbeat]);

  const getDocRefs = useCallback(() => {
    const refs = useEditorState.getState().documentationRefs;
    // Convert Map values to array of "pseudo-refs" that postMessageToRef expects
    return Array.from(refs.values()).map(el => ({ current: { contentWindow: el.contentWindow } }));
  }, []);


  const handleLTIAchievement = async (achievementId: string | number, token: string) => {
    const currentAchievements = useEditorState.getState().achievements || [];
    if (currentAchievements.length === 0) return;

    const updatedAchievements = currentAchievements.map((achievement) =>
      achievement.id === achievementId
        ? { ...achievement, is_achieved: true }
        : achievement,
    );
    useEditorState.setState({ achievements: updatedAchievements });
    useEditorState.setState({ saveStatus: "NEEDSAVE" })

    await actionMap.score_results({ data: { score: calculateLtiScore() } }, token);
  }


  const postMessage = useCallback(postMessageToRef, []);

  const parentPostMessage = useCallback(postMessageToParent, []);

  interface ActionMap {
    [key: string]: (...args: any[]) => void;
  }

  const actionMap: ActionMap = {
    listFiles: async function () {
      const files = await FS.list("/tmp");

      postMessage([godotContainerRef, ...getDocRefs()], {
        action: "init",
        key: "/",
        data: files,
      });
    },

    fsList: async function (path: string, _?: string) {
      const files = await FS.list(path);
      postMessage([godotContainerRef, ...getDocRefs()], {
        action: "fsListResponse",
        key: "/",
        data: files,
        status: "success",
        filePath: path,
      });
    },

    init: async function () {
      this.listFiles();
    },

    getToken: async function (message: any, token: string) {
      postMessage([godotContainerRef, ...getDocRefs()], {
        action: "getTokenResponse",
        token: token,
      });
    },

    getUserRoles: async function () {
      const roles = useEditorState.getState().sessionUser?.roles ?? [];
      postMessage([godotContainerRef, ...getDocRefs()], {
        action: "getUserRolesResponse",
        roles,
      });
    },

    godotLoadingProgress: async function ({ percentage }: { percentage: number }) {
      console.log("[GodotLoading] progress:", percentage);
    },

    godotLoadingComplete: async function () {
      console.log("[GodotLoading] complete");
      useEditorState.setState({ godotLoadingComplete: true });
    },

    achieve: async function (message: any, token: any) {
      try {
        const id = parseAchievementId(message.data);
        if (useEditorState.getState().platform === "lti") {
          handleLTIAchievement(id, token);
          return;
        }

        const ws = sessionSocket.ws;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          // Not connected. Register achievement locally but not send to the server.
          registerAchievementLocally(id);
          return;
        }



        //TODO(Baturay):: What happens if user manually send achievement to the server? 
        ws.send(JSON.stringify({
          type: "achieve",
          ...(typeof id === "string" ? { achievement_slug: id } : { achievement_id: id }),
        }));

        registerAchievementLocally(id);

      } catch (e) {
        Sentry.captureException(
          new AchievementError("Error in achieve function", e as Error),
          {
            level: "info"
          }
        );
        throw e;
      }
    },

    activity: async function (message: any, token: any) {
      debouncedHeartbeat();
    },

    score_results: async function (message: any, token: any) {
      await getScore(token, encryptMessage(message.data));
    },

    submit_quiz: async function (message: any, token: any) {
      console.log("[editor_v4] actionMap.submit_quiz called with:", message);
      const results: any = await setQuizResult(
        token,
        message.quiz,
        encryptMessage(message.data),
      );
      console.log("[editor_v4] setQuizResult success:", results);

      const isComplementary = project?.project_id?.startsWith("complementary;");
      const targetRefs = isComplementary ? [godotContainerRef, ...getDocRefs()] : [...getDocRefs()];

      postMessage(targetRefs, {
        action: "submit_quiz_response",
        data: results?.data || results,
      });
    },

    quiz_results: async function (message: any, token: any) {
      const results: any = await getQuizResults(token, message.quiz);

      const isComplementary = project?.project_id?.startsWith("complementary;");
      const targetRefs = isComplementary ? [godotContainerRef, ...getDocRefs()] : [...getDocRefs()];

      postMessage(targetRefs, {
        action: "quiz_results_response",
        data: results.data,
      });
    },

    get_quiz_data: async function (message: any, token: any) {
      console.log("[editor_v4] actionMap.get_quiz_data called with:", message);
      try {
        const questions: any = await getQuizQuestions(token, message.quiz, useEditorState.getState().language);
        let pastResults: any = null;
        try {
          pastResults = await getQuizResults(token, message.quiz);
          console.log("[editor_v4] getQuizResults success, pastResults:", pastResults);
        } catch (e) {
          console.warn("[editor_v4] Could not fetch past quiz results:", e);
        }

        console.log("[editor_v4] getQuizQuestions success, response:", questions);

        const isComplementary = project?.project_id?.startsWith("complementary;");
        const targetRefs = isComplementary ? [godotContainerRef, ...getDocRefs()] : [...getDocRefs()];

        console.log("[editor_v4] Sending quiz_data to targetRefs, isComplementary:", isComplementary, targetRefs);
        postMessage(targetRefs, {
          action: "quiz_data",
          quiz: {
            quizId: message.quiz,
            questions: questions?.data || questions,
            results: pastResults?.data || null
          }
        });
      } catch (error) {
        console.error("[editor_v4] Failed to fetch quiz data", error);
      }
    },

    getachievements: async function (message: any, token: any) {
      // TODO (Etkin): Documentation's ready function is this for now. Fix it.
      const response = { action: "initachievements", data: [] as any[] };
      const data = await getAchievements(token, language);

      let pendingAchievements: (string | number)[] = [];
      try {
        const { getPendingAchievementsList } = await import("@/utils/pendingAchievements");
        pendingAchievements = getPendingAchievementsList();
      } catch (e) {
        console.warn("Failed to load pending achievements", e);
      }

      if (data.message?.includes("Success")) {
        response.data = (data.data || []).map((ach: any) => {
          const parsedId = ach.slug || ach.id;
          const isPending = pendingAchievements.includes(parsedId) || pendingAchievements.includes(ach.id);
          return {
            ...ach,
            id: parsedId,
            is_achieved: ach.is_achieved || isPending
          };
        });
      }
      else console.error({ action: message.action, message: data.message });

      const isComplementary = project?.project_id?.startsWith("complementary;");
      const targetRefs = isComplementary ? [godotContainerRef, ...getDocRefs()] : [...getDocRefs()];

      postMessage(targetRefs, response);
    },

    setEditorLayout: async function (message: any) {
      const { layout } = message;
      setLayout(layout);
      postMessage(godotContainerRef, {
        action: "setEditorLayoutResponse",
        status: "eventReceived",
      });
    },

    highlightBlock: async function (message: any) {
      if (useEditorState.getState().blocklyState === "INITED") {
        useEditorState.getState().highlightBlock(message);
      } else {
        useEditorState.getState().highLightQueue.push(message);
      }
    },

    highlightToolboxBlock: async function (message: any) {
      if (useEditorState.getState().blocklyState === "INITED") {
        useEditorState.getState().highlightToolboxBlock(message);
      } else {
        useEditorState.getState().highLightQueue.push(message);
      }
    },

    subscribeFile: async function (message: any) {
      const { path } = message;
      if (!window.fileSubscribers) {
        window.fileSubscribers = [];
      }
      if (!window.fileSubscribers.includes(path)) {
        window.fileSubscribers.push(path);
      }
    },

    unsubscribeFile: async function (message: any) {
      const { path } = message;
      if (window.fileSubscribers && window.fileSubscribers.includes(path)) {
        window.fileSubscribers = window.fileSubscribers.filter(
          (p: string) => p !== path,
        );
      }
    },

    fullScreenToggleTabset: async function (message: any) {
      const { tabset, force } = message;
      if (force) {
        const node: any = model.getNodeById(tabset);
        if (node) {
          if (force === "maximize") {
            if (!node.isMaximized())
              model.doAction(Actions.maximizeToggle(tabset));
          }
          if (force === "minimize") {
            if (node.isMaximized())
              model.doAction(Actions.maximizeToggle(tabset));
          }
        }
      } else {
        model.doAction(Actions.maximizeToggle(tabset));
      }
    },

    getTabsetInformation: async function (message: any) {
      const { tabset } = message;
      const node: any = model.getNodeById(tabset);
      if (node) {
        const tabset = {
          id: node.getId(),
          isMaximized: node.isMaximized(),
        };
        postMessage(godotContainerRef, {
          action: "getTabsetInformationResponse",
          tabset: tabset,
        });
      } else {
        postMessage(godotContainerRef, {
          action: "getTabsetInformationResponse",
          error: "Tabset not found",
        });
      }
    },

    showQuiz: async function (message: any) {
      const { quizId } = message;
      showModal(t("qa_title"), <QA quizId={parseQAId(quizId)} />, { width: 1200 });
    },

    readFileContentRequest: async function (filePath: string, _?: string) {
      try {
        const content = await FS.read(filePath);

        postMessage([godotContainerRef, ...getDocRefs()], {
          action: "readFileContentResponse",
          filePath: filePath,
          content: content,
          success: true,
        });
      } catch (error) {
        postMessage([godotContainerRef, ...getDocRefs()], {
          action: "readFileContentResponse",
          filePath: filePath,
          success: false,
        });
        Sentry.captureException(new FileSystemError("Failed to read file content", error as Error));
      }
    },

    writeFileContentRequest: async function (
      filePath: string,
      content: string,
    ) {
      try {
        if (filePath === "/.blockly/generateblocks.js") {
          const prevGenerateBlocksFile = useEditorState.getState().generateBlocksFile;
          useEditorState.setState({ generateBlocksFile: content });
          if (prevGenerateBlocksFile !== content) {

            useEditorState.setState({ isBlocklyRefreshValid: true });
          }
          postMessage([godotContainerRef, ...getDocRefs()], {
            action: "writeFileContentResponse",
            filePath: filePath,
            success: true,
          });
          return;
        }

        if (filePath === "/.blockly/initblocks.js") {
          const prevInitBlocksFile = useEditorState.getState().initBlocksFile;
          useEditorState.setState({ initBlocksFile: content });

          if (prevInitBlocksFile !== content) {

            useEditorState.setState({ isBlocklyRefreshValid: true });
          }

          postMessage([godotContainerRef, ...getDocRefs()], {
            action: "writeFileContentResponse",
            filePath: filePath,
            success: true,
          });
          return;
        }

        if (filePath === "/.blockly/toolbox.json") {
          const prevToolboxFile = useEditorState.getState().toolboxFile;
          useEditorState.setState({ toolboxFile: content });

          if (prevToolboxFile !== content) {
            useEditorState.setState({ isBlocklyRefreshValid: true });
          }

          postMessage([godotContainerRef, ...getDocRefs()], {
            action: "writeFileContentResponse",
            filePath: filePath,
            success: true,
          });
          return;
        }

        await FS.write(filePath, content, { useDebouncer: false });

        const blocklyIndex = useEditorState.getState().blocklyCodePath;
        const pythonIndex = useEditorState.getState().pythonCodePath;
        if (filePath === blocklyIndex || filePath === pythonIndex) {
          useEditorState.setState({ isBlocklyRefreshValid: true });
          setBlocklyRefreshKey();
        }

        const ls = await FS.list("/tmp");
        postMessage([godotContainerRef, ...getDocRefs()], {
          action: "writeFileContentResponse",
          filePath: filePath,
          success: true,
          data: ls,
        });


      } catch (error) {
        postMessage([godotContainerRef, ...getDocRefs()], {
          action: "writeFileContentResponse",
          filePath: filePath,
          success: false,
        });
        Sentry.captureException(new FileSystemError("Failed to write file content", error as Error));
      }
    },

    deleteFileContentRequest: async function (filePath: string) {
      try {
        await FS.delete(filePath);
        const ls = await FS.list("/tmp");
        postMessage([godotContainerRef, ...getDocRefs()], {
          action: "deleteFileContentResponse",
          filePath: filePath,
          success: true,
          data: ls,
        });
      } catch (error) {
        postMessage([godotContainerRef, ...getDocRefs()], {
          action: "deleteFileContentResponse",
          filePath: filePath,
          success: false,
        });
        Sentry.captureException(new FileSystemError("Failed to delete file content", error as Error));
      }
    },

    projectInfoRequest: async function () {
      try {
        postMessage(godotContainerRef, {
          action: "projectInfoResponse",
          project: project,
        });
      } catch (error) {
        postMessage(godotContainerRef, {
          action: "projectInfoResponse",
          project: null,
        });
        Sentry.captureException(new CommunicationError("Failed to get project info", error as Error));
      }
    },

    refreshBlockly: async function () {
      if (!useEditorState.getState().isBlocklyRefreshValid) {
        postMessageToRef(godotContainerRef, {
          action: "setEditorLayoutResponse",
          status: "blocklyIsReady",
        });
        return
      };
      setBlocklyRefreshKey();
    },

    getImageOfXML: async function ({ xmlCode, xmlName }: { xmlCode: string, xmlName: string }) {
      if (!xmlCode) return;

      if ((window as any).shadowWorkspace) {
        const shadowWorkspace = (window as any).shadowWorkspace;

        const xmlDom = Blockly.utils.xml.textToDom(xmlCode);
        Blockly.Xml.clearWorkspaceAndLoadFromXml(xmlDom, shadowWorkspace);

        const image_base64_string = await downloadBlocklyScreenshot();
        const raw = image_base64_string.replace(/^data:image\/png;base64,/, "");
        postMessageToRef(godotContainerRef, {
          action: "onGetXMLImage",
          image_base64_string: raw,
          xmlName: xmlName
        });
      }
    },

    setCircleFlow: async function ({ circles }: { circles: CircleFlowGroup[] }) {
      setCircleFlow(circles ?? null);
    },

    toggleCircleFlowSideBar: async function ({ collapsed }: { collapsed?: boolean | null }) {
      const next = typeof collapsed === "boolean" ? collapsed : !useEditorState.getState().collapsedSidebar;
      setCollapsedSidebar(next);
    },

    reportError: async function (postMessage: any) {
      const { message, metadata } = postMessage;

      Sentry.addBreadcrumb({
        category: "iframe.error",
        message: message || "Unknown iframe error",
        level: "error",
        data: {
          ...metadata,
        },
      });

      Sentry.captureException(
        new IframeError(message || "Iframe reported an error", undefined, {
          ...metadata,
        })
      );
    },

    sessionStop: async function () {
      await idleShutDown(accessToken || "");
    },

    gitTrigger: debounce(async function () {
      try {
        // const info = await gitSyncHandler(["tmp"], true);
        postMessage(godotContainerRef, {
          action: "gitTriggerResponse",
          status: "success",
          commitHash: "",
        });
        parentPostMessage({
          action: "gitTriggerResponse",
          status: "success",
          commitHash: "",
        });
      } catch (error) {
        postMessage(godotContainerRef, {
          action: "gitTriggerResponse",
          status: "error",
          commitHash: null,
        });
        parentPostMessage({
          action: "gitTriggerResponse",
          status: "error",
          commitHash: null,
        });
      }
    }, 5000),

    dispatch: async function (event: any) {
      postMessageToRef([...getDocRefs()], event);
    },

    RIDERS_CHECKPOINT: async function (event: any) {
      const isComplementary = project?.project_id?.startsWith("complementary;");
      const targetRefs = isComplementary ? [godotContainerRef, ...getDocRefs()] : [...getDocRefs()];
      postMessage(targetRefs, event);
    },
  };

  const godot_iframe_handler = async (action: {
    action: string;
    frame: "gd" | "doc";
    data: any;
  }) => {
    if (action.action === "ready") {
      const result: any = [];

      for (const filename of action.data) {
        result.push({
          name: filename,
          content: await FS.read(filename),
        });
      }

      const message = JSON.stringify({
        action: "init",
        key: "/",
        data: result,
      });

      godotContainerRef?.current?.contentWindow.postMessage(message, "*");
    } else if (action.action === "save") {
      for (const item of action.data) {
        FS.write(item.name, item.content);
      }
    }
  };

  const doc_iframe_handler = async (action: {
    action: string;
    frame: "gd" | "doc";
    data: any;
  }) => {
    if (action.action === "ready") {
      const result: any = [];

      for (const filename of action.data) {
        result.push({
          name: filename,
          content: await FS.read(filename),
        });
      }

      const message = JSON.stringify({
        action: "init",
        key: "/",
        data: result,
      });

      getDocRefs().forEach(ref => ref.current?.contentWindow?.postMessage(message, "*"));
    } else if (action.action === "save") {
      for (const item of action.data) {
        FS.write(item.name, item.content);
      }
    }
  };

  const messageListener = useCallback(async (event: any) => {
    // if (event.origin !== "https://d32lhafmwoak77.cloudfront.net") {
    //   return;
    // }
    // Get actionType and Frame info from the message then take actions.
    //console.log("📨 messageListener triggered:", event);
    let message: any = {};
    if (typeof event.data === "string") {
      try {
        message = JSON.parse(event.data);
      } catch {
        message = { action: event.data }; // düz string ise action olarak ata
      }
    } else if (typeof event.data === "object") {
      message = event.data;
    }
    const actionType: string = message.action;

    // 1. Artık event.data'yı string olarak da kontrol et
    if (event.data === "ANDROID_READY") {
      console.log("✅ Android is ready (string)!");
      // Port varsa WEB_READY geri gönder
      if (event.ports && event.ports[0]) {
        event.ports[0].postMessage("WEB_READY");
      }
    } else if (event.data === "sessionStop") {
      console.log("⏹ Session stopped by Android app (string).");
      idleShutDown(accessToken || "").then();
    }

    // 2. Port mesajlarını da mutlaka dinle
    if (event.ports && event.ports[0]) {
      const port = event.ports[0];
      port.onmessage = (e: any) => {
        console.log("📩 Android → Web (via port):", e.data);

        if (e.data === "ANDROID_READY") {
          console.log("✅ Android is ready (via port)!");
          port.postMessage("WEB_READY");
        }
        if (e.data === "sessionStop") {
          console.log("⏹ Session stop requested from Android app (via port).");
          idleShutDown(accessToken || "").then();
        }
      };

      // İlk bağlantıda web'in hazır olduğunu bildir
      port.postMessage("WEB_READY");
    }

    if (
      [
        "readFileContentRequest",
        "writeFileContentRequest",
        "fsList",
        "deleteFileContentRequest",
      ].includes(actionType)
    ) {
      asyncExecutionQue.submit({
        fn: actionMap[actionType] as any,
        parameters: [message.filePath, message.content]
      });
      return;
    }

    if ([
      "highlightBlock",
      "highlightToolboxBlock",
    ].includes(actionType)) {
      asyncExecutionQue.submit({
        fn: actionMap[actionType] as any,
        parameters: [message]
      });
      return;
    }

    if (
      [
        "init",
        "listFiles",
        "gitTrigger",
        "projectInfoRequest",
        "refreshBlockly",
        "getImageOfXML",
        "setEditorLayout",
        "sessionStop",
        "getUserRoles",
        "godotLoadingProgress",
        "godotLoadingComplete",
      ].includes(message.action)
    )
      actionMap[actionType](message, project);
    else if (
      [
        "achieve",
        "score_results",
        "getachievements",
        "activity",
        "submit_quiz",
        "quiz_results",
        "get_quiz_data",
        "getToken",
        "subscribeFile",
        "unsubscribeFile",
        "fullScreenToggleTabset",
        "getTabsetInformation",
        "showQuiz",
        "reportError",
        "dispatch",
        "RIDERS_CHECKPOINT",
        "setCircleFlow",
        "toggleCircleFlowSideBar"
      ].includes(message.action)
    ) {
      return actionMap[actionType](message, accessToken);
    }
    // Have we got any scenarios that we have no message.action?
    else if (message.frame) {
      if (message.action) {
        console.warn(
          "This message action is not handled. Probably deprecated or not supported for this action:",
          message.action,
        );
      } else {
        console.warn(
          "Messages without action type are not supported by the editor.",
        );
      }

      if (message.frame === "gd") godot_iframe_handler(message);
      else doc_iframe_handler(message);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", messageListener);
    return () => {
      window.removeEventListener("message", messageListener);
    };
  }, []);

  useEffect(() => {
    const writeListenerId = FS.events.registerEventListener("WRITE", async (path: string, content: string) => {
      const postFiles = [];

      if (window.fileSubscribers?.includes(path)) {
        postFiles.push({ src: path, content: content });
      }

      if (postFiles.length > 0) {
        postMessage(godotContainerRef, {
          action: "fileChanged",
          files: postFiles,
        });
      }

    });

    return () => { FS.events.unregisterEventListener("WRITE", writeListenerId) }

  }, [godotContainerRef]);

  return (
    <CommunicationContext.Provider
      value={{
        postMessage,
        parentPostMessage,
        encryptMessage,
      }}
    >
      {children}
    </CommunicationContext.Provider>
  );
};

Communication.context = CommunicationContext;
